import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocalization } from '../contexts/LocalizationContext';
import api, { getApiImageUrl } from '../services/api';
import LevelUpAnimation from './LevelUpAnimation';
import './ResultScreen.css';

const ResultScreen = ({ refreshStarCount, currentPlayer }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language } = useLocalization();
  const resultScreenRef = useRef(null);
  
  // Helper function for localization with fallbacks
  const getTranslation = (key, fallback) => {
    const translation = t(key);
    if (translation === key || !translation) {
      console.warn(`Translation missing for key: ${key}, using fallback: ${fallback}`);
      return fallback;
    }
    return translation;
  };
  
  const { result, moduleName, moduleId, currentCardId, cardData, userAnswer, isReviewMode } = location.state || {};
  const [autoAdvanceTimer, setAutoAdvanceTimer] = useState(4);
  const [showResult, setShowResult] = useState(false);
  const [showLevelUpAnimation, setShowLevelUpAnimation] = useState(false);
  const [levelUpData, setLevelUpData] = useState(null);
  const audioPlayedRef = useRef(false);
  const [explanationImageUrl, setExplanationImageUrl] = useState(null);

  // Answer images are gated on the backend. Fetch with the Authorization header
  // (and the active player scoped via player_id) instead of putting the JWT in
  // the <img> URL, where it would leak into server/proxy logs.
  useEffect(() => {
    const imagePath = result?.resultScreen?.richFeedback?.image;
    if (!imagePath) {
      setExplanationImageUrl(null);
      return;
    }
    let objectUrl = null;
    let cancelled = false;
    api.get(imagePath, {
      params: currentPlayer?.id ? { player_id: currentPlayer.id } : {},
      responseType: 'blob'
    })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setExplanationImageUrl(objectUrl);
      })
      .catch((err) => {
        console.error('Failed to load explanation image:', err);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [result, currentPlayer]);


  useEffect(() => {
    if (!result) {
      navigate('/play');
      return;
    }

    // Reset audio flag for new result
    audioPlayedRef.current = false;

    // Refresh star count if stars were earned
    if (result.correct && result.resultScreen?.rewardCoins > 0 && refreshStarCount) {
      refreshStarCount();
    }

    // Check for levelup or evolution animations
    if (result.resultScreen?.animation === 'dragon_evolution') {
      const currentLevel = result.resultScreen.rewardMessageParams?.level || 1;
      setLevelUpData({
        isEvolution: true,
        oldLevel: currentLevel === 1 ? 0 : currentLevel - 1,
        newLevel: currentLevel,
        currentEvolution: result.resultScreen.currentEvolution || 'egg',
        nextEvolution: result.resultScreen.nextEvolution || 'baby',
        playerId: result.resultScreen.playerId
      });
      setShowLevelUpAnimation(true);
    } else if (result.resultScreen?.rewardMessageKey === 'result.levelUp') {
      const currentLevel = result.resultScreen.rewardMessageParams?.level || 2;
      // Check if this is actually the first module completion (egg hatching)
      if (currentLevel === 1) {
        setLevelUpData({
          isEvolution: true,
          oldLevel: 0,
          newLevel: 1,
          currentEvolution: 'egg',
          nextEvolution: 'baby',
          playerId: currentPlayer?.id
        });
      } else {
        setLevelUpData({
          isEvolution: false,
          oldLevel: currentLevel - 1,
          newLevel: currentLevel
        });
      }
      setShowLevelUpAnimation(true);
    }

    // Auto-focus the result screen for keyboard events
    if (resultScreenRef.current) {
      resultScreenRef.current.focus();
    }

    // Play sound effect if provided - only once per result
    if (result.resultScreen?.soundEffect && !audioPlayedRef.current) {
      audioPlayedRef.current = true;
      const audio = new Audio(result.resultScreen.soundEffect);
      audio.play().catch(err => {
        console.log('Failed to play sound effect:', err);
      });
    }

    // Show result with animation
    const showTimer = setTimeout(() => setShowResult(true), 100);

    // Handle auto-advance for quick results - but ONLY if answer was correct
    if (result.resultScreen?.type === 'quick' && result.correct) {
      const interval = setInterval(() => {
        setAutoAdvanceTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            handleNext();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        clearInterval(interval);
        clearTimeout(showTimer);
      };
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => clearTimeout(showTimer);
  }, [result, navigate]);

  const handleNext = () => {
    // In review mode, go back to module review screen
    if (isReviewMode) {
      navigate(-1);  // Go back to ModuleReviewScreen
      return;
    }
    
    // Check if this is a dragon selection result
    if (result.resultScreen?.type === 'dragon_selection') {
      navigate('/dragon-selection');
      return;
    }
    
    if (result.nextCard) {
      // Navigate to next card using module ID (backend will select the next card)
      navigate(`/play/${moduleId || moduleName}`);
    } else {
      // No more cards, return to modules
      navigate('/play');
    }
  };

  const handleKeyPress = (e) => {
    // For incorrect answers, only Enter key should proceed
    if (!result.correct) {
      if (e.key === 'Enter') {
        handleNext();
      }
      return;
    }
    
    if (result.resultScreen?.type === 'long' || result.resultScreen?.type === 'dragon_selection') {
      // For long results and dragon selection, any key press advances
      handleNext();
    } else if (result.resultScreen?.type === 'quick' && e.key === 'Enter') {
      // For quick results, Enter key skips the countdown
      handleNext();
    }
  };

  if (!result) {
    return null;
  }

  const { correct, resultScreen, nextCard } = result;
  const isQuickResult = resultScreen.type === 'quick';
  const isLongResult = resultScreen.type === 'long';

  return (
    <>
      {showLevelUpAnimation && levelUpData && (
        <LevelUpAnimation
          oldLevel={levelUpData.oldLevel}
          newLevel={levelUpData.newLevel}
          currentEvolution={levelUpData.currentEvolution}
          nextEvolution={levelUpData.nextEvolution}
          isEvolution={levelUpData.isEvolution}
          playerId={levelUpData.playerId || currentPlayer?.id}
          onComplete={() => setShowLevelUpAnimation(false)}
        />
      )}
      <div 
        ref={resultScreenRef}
        className={`result-screen ${showResult ? 'show' : ''} ${correct ? 'correct' : 'incorrect'}`}
        onKeyDown={handleKeyPress}
        tabIndex="0"
      >
        <div className="result-content">
        {/* Result Icon */}
        <div className="result-icon">
          {correct ? (
            <div className="success-icon">✅</div>
          ) : (
            <div className="error-icon">❌</div>
          )}
        </div>

        {/* For incorrect answers or review mode, show the original question and correct answer */}
        {(isReviewMode || !correct) && cardData && cardData.content && (
          <div className="incorrect-answer-review">
            {/* Display images at the top */}
            {cardData.content.images && cardData.content.images.length > 0 && (
              <div className="card-images">
                {cardData.content.images.map((image, index) => (
                  <img 
                    key={index}
                    src={getApiImageUrl(image.url)} 
                    alt={`${getTranslation('cards.cardImage', 'Card image')} ${index + 1}`}
                    className={`card-image ${image.position}`}
                    onError={(e) => {
                      console.error(`Failed to load image: ${image.url}`);
                      e.target.style.display = 'none';
                    }}
                  />
                ))}
              </div>
            )}

            {/* Display question text */}
            <div className="question-text">
              {cardData.content.question && (
                <div className="question">
                  {cardData.content.question.split('\n').map((line, index) => (
                    <p key={index}>{line}</p>
                  ))}
                </div>
              )}
              
              {/* Handle lesson text format */}
              {cardData.content.text && (
                <div className="lesson-content">
                  {cardData.content.text.map((paragraph, index) => (
                    <div key={index} className="lesson-paragraph">
                      <p>{paragraph.paragraph}</p>
                      {paragraph.imageAfter && (
                        <img 
                          src={getApiImageUrl(`/api/images/${moduleId}/${paragraph.imageAfter}`)}
                          alt={`${getTranslation('cards.lessonImage', 'Lesson image')} ${index + 1}`}
                          className="lesson-image"
                          onError={(e) => {
                            console.error(`Failed to load lesson image: ${paragraph.imageAfter}`);
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Show multiple choice options if available */}
            {cardData.content?.options && cardData.content.options.length > 0 && (
              <div className="multiple-choice-review">
                <p className="options-label">{getTranslation('result.availableOptions', 'Options:')}</p>
                {cardData.content.options.map((option) => {
                  const isUserAnswer = (userAnswer || result.userAnswer || result.resultScreen?.messageParams?.userAnswer) === option.key;
                  // Check multiple sources for correct answer and ensure proper comparison
                  const correctAnswerValue = result.correctAnswer || result.resultScreen?.messageParams?.correctAnswer;
                  const isCorrectAnswer = correctAnswerValue === option.key ||
                                         correctAnswerValue === option.value ||
                                         (correctAnswerValue && correctAnswerValue.toString().toUpperCase() === option.key.toUpperCase());

                  // Check if this is an image-based option (no text value)
                  const hasTextValue = option.value && option.value.trim().length > 0;

                  return (
                    <div key={option.key} className={`option-review ${
                      isUserAnswer ? 'user-selected' : ''
                    } ${isCorrectAnswer ? 'correct-option' : ''}`}>
                      <span className="option-key">{option.key})</span>
                      {hasTextValue && <span className="option-value">{option.value}</span>}
                      {!hasTextValue && <span className="option-value-placeholder">{getTranslation('result.seeImageAbove', '(see image above)')}</span>}
                      {isUserAnswer && (
                        <span className="option-indicator user">← {getTranslation('taskReview.yourAnswer', 'Your answer')}</span>
                      )}
                      {isCorrectAnswer && (
                        <span className="option-indicator correct">← {getTranslation('taskReview.correctAnswer', 'Correct answer')}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Show user's answer and correct answer for non-multiple choice */}
            {!cardData.content?.options && (
              <div className="answers-comparison">
                {(() => {
                  // Check multiple sources for user answer
                  const userAnswerValue = userAnswer || result.userAnswer || result.resultScreen?.messageParams?.userAnswer;
                  
                  // Always show the user answer section (even if empty)
                  return (
                    <div className="user-answer-box">
                      <p>{getTranslation('taskReview.yourAnswer', 'Your answer')}</p>
                      <strong>{userAnswerValue || getTranslation('result.noAnswer', 'No answer')}</strong>
                    </div>
                  );
                })()}
                <div className="correct-answer-box">
                  <p>{getTranslation('taskReview.correctAnswer', 'Correct answer')}</p>
                  <strong>{result.resultScreen?.messageParams?.correctAnswer || result.correctAnswer}</strong>
                </div>
              </div>
            )}
          </div>
        )}

        {/* For correct answers (non-review mode) or when card data is missing, show regular result message */}
        {!isReviewMode && (correct || !cardData || !cardData.content) && (
          <div className="result-message">
            <h2>
              {resultScreen.messageKey ? 
                t(resultScreen.messageKey, resultScreen.messageParams || {}) : 
                resultScreen.message}
            </h2>
            {resultScreen.rewardMessageKey && (
              <p className="reward-message">
                {(() => {
                  const localizedCollectibleNames = {
                    hr: resultScreen.collectibleNameHr,
                    nl: resultScreen.collectibleNameNl
                  };
                  const translationParams = {
                    collectibleName: localizedCollectibleNames[language] || resultScreen.collectibleName,
                    ...resultScreen.rewardMessageParams
                  };
                  const translatedText = t(resultScreen.rewardMessageKey, translationParams);
                  console.log('Translation debug:', {
                    key: resultScreen.rewardMessageKey,
                    params: translationParams,
                    result: translatedText
                  });
                  return translatedText;
                })()}
              </p>
            )}
          </div>
        )}

        {/* Rewards Section - don't show in review mode */}
        {!isReviewMode && (resultScreen.rewardCoins > 0 || resultScreen.badge || resultScreen.specialImage) && (
          <div className="rewards-section">
            {/* Coins */}
            {resultScreen.rewardCoins > 0 && (
              <div className="reward-item coins">
                <span>+{resultScreen.rewardCoins} ⭐</span>
              </div>
            )}

            {/* Badge */}
            {resultScreen.badge && (
              <div className="reward-item badge">
                <div className="badge-icon">🏆</div>
                <div className="badge-info">
                  <h3>{resultScreen.badge.name}</h3>
                  <p>{resultScreen.badge.description}</p>
                </div>
              </div>
            )}

            {/* Special Image */}
            {resultScreen.specialImage && (
              <div className="reward-item special-image">
                <img 
                  src={getApiImageUrl(resultScreen.specialImage)} 
                  alt="Special reward"
                  className="reward-image"
                />
              </div>
            )}
          </div>
        )}

        {/* Rich Feedback Section */}
        {resultScreen.richFeedback && (
          <div className="rich-feedback-section">
            {resultScreen.richFeedback.text && (
              <div className="feedback-text">
                <h3>{getTranslation('result.explanation', 'Explanation')}</h3>
                <div className="explanation-content">
                  {resultScreen.richFeedback.text.split('\n').map((line, index) => (
                    <p key={index}>{line}</p>
                  ))}
                </div>
              </div>
            )}
            {resultScreen.richFeedback.image && explanationImageUrl && (
              <div className="feedback-image">
                <img
                  src={explanationImageUrl}
                  alt={getTranslation('result.explanationImage', 'Explanation image')}
                  className="explanation-image"
                />
              </div>
            )}
          </div>
        )}

        {/* Animation Area - removed placeholder, animation is handled by LevelUpAnimation component */}

        {/* Action Buttons */}
        <div className="action-section">
          {isReviewMode ? (
            /* Review mode - just show back button */
            <div className="review-mode-actions">
              <button onClick={handleNext} className="back-button">
                {getTranslation('result.backToReview', 'Back to Module Review')}
              </button>
            </div>
          ) : !correct ? (
            /* For incorrect answers, require Enter key confirmation */
            <div className="incorrect-result-actions">
              <p className="continue-instruction">
                {getTranslation('result.pressEnterToContinue', 'Press Enter to continue')}
              </p>
              <button onClick={handleNext} className="continue-button">
                {nextCard ? getTranslation('result.continue', 'Continue') : getTranslation('result.backToModules', 'Back to modules')}
              </button>
            </div>
          ) : isQuickResult ? (
            <div className="quick-result-actions">
              <p className="auto-advance">
                {autoAdvanceTimer}s
              </p>
              <button onClick={handleNext} className="next-button">
                {nextCard ? getTranslation('result.nextCard', 'Next card') : getTranslation('result.backToModules', 'Back to modules')}
              </button>
            </div>
          ) : isLongResult ? (
            <div className="long-result-actions">
              <p className="continue-instruction">
                {getTranslation('result.pressAnyKey', 'Press any key to continue')}
              </p>
              <button onClick={handleNext} className="continue-button">
                {nextCard ? getTranslation('result.continue', 'Continue') : getTranslation('result.backToModules', 'Back to modules')}
              </button>
            </div>
          ) : (
            <button onClick={handleNext} className="next-button">
              {nextCard ? getTranslation('result.nextCard', 'Sljedeća kartica') : getTranslation('result.backToModules', 'Povratak na module')}
            </button>
          )}
        </div>

      </div>

    </div>
    </>
  );
};

export default ResultScreen;